"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DefaultDashboardLayout = void 0;
var tslib_1 = require("tslib");
// (C) 2020-2023 GoodData Corporation
var react_1 = tslib_1.__importStar(require("react"));
var sdk_model_1 = require("@gooddata/sdk-model");
var util_1 = require("@gooddata/util");
var max_1 = tslib_1.__importDefault(require("lodash/max"));
var model_1 = require("../../model");
var DashboardLayoutWidget_1 = require("./DashboardLayoutWidget");
var DefaultDashboardLayoutRenderer_1 = require("./DefaultDashboardLayoutRenderer");
var RenderModeAwareDashboardLayoutSectionRenderer_1 = require("./DefaultDashboardLayoutRenderer/RenderModeAwareDashboardLayoutSectionRenderer");
var RenderModeAwareDashboardLayoutSectionHeaderRenderer_1 = require("./DefaultDashboardLayoutRenderer/RenderModeAwareDashboardLayoutSectionHeaderRenderer");
var DefaultDashboardLayoutUtils_1 = require("./DefaultDashboardLayoutUtils");
var dragAndDrop_1 = require("../dragAndDrop");
var widgets_1 = require("../../widgets");
var EmptyDashboardLayout_1 = require("./EmptyDashboardLayout");
/**
 * Get dashboard layout for exports.
 *  - Create new extra rows if current row has width of widgets greater than 12
 *
 * @internal
 * @param layout - dashboard layout to modify
 * @returns transformed layout
 */
function getDashboardLayoutForExport(layout) {
    var dashLayout = DefaultDashboardLayoutRenderer_1.DashboardLayoutBuilder.for(layout);
    var layoutFacade = dashLayout.facade();
    var sections = layoutFacade.sections();
    var screenSplitSections = sections.map(function (section) { return ({
        items: section.items().asGridRows("xl"),
        header: section.header(),
    }); });
    dashLayout.removeSections();
    screenSplitSections.forEach(function (wrappedSection) {
        wrappedSection.items.forEach(function (rowSection, index) {
            // Also ensure that all items in a section have equal grid height - the max one.
            // This simulates non-export mode, where the flex-box stretches
            // all the items in a row to the equal height.
            // This is necessary as we have no control over the custom widgets heights.
            var sectionItemsGridHeights = rowSection.map(function (item) { return item.size().xl.gridHeight; });
            var maxGridHeight = max_1.default(sectionItemsGridHeights);
            dashLayout.createSection(function (section) {
                rowSection.forEach(function (item) {
                    if (index === 0) {
                        section.header(wrappedSection.header);
                    }
                    section.createItem(tslib_1.__assign(tslib_1.__assign({}, item.size().xl), { gridHeight: maxGridHeight }), function (i) {
                        return i.widget(item.widget());
                    });
                });
                return section;
            });
        });
    });
    return dashLayout.build();
}
var itemKeyGetter = function (keyGetterProps) {
    var widget = keyGetterProps.item.widget();
    if (sdk_model_1.isWidget(widget)) {
        return sdk_model_1.objRefToString(widget.ref);
    }
    return keyGetterProps.item.index().toString();
};
/**
 * @alpha
 */
exports.DefaultDashboardLayout = function (props) {
    var onFiltersChange = props.onFiltersChange, onDrill = props.onDrill, onError = props.onError;
    var layout = model_1.useDashboardSelector(model_1.selectLayout);
    var isLayoutEmpty = model_1.useDashboardSelector(model_1.selectIsLayoutEmpty);
    var enableWidgetCustomHeight = model_1.useDashboardSelector(model_1.selectEnableWidgetCustomHeight);
    var insights = model_1.useDashboardSelector(model_1.selectInsightsMap);
    var isExport = model_1.useDashboardSelector(model_1.selectIsExport);
    var renderMode = model_1.useDashboardSelector(model_1.selectRenderMode);
    var getInsightByRef = react_1.useCallback(function (insightRef) {
        return insights.get(insightRef);
    }, [insights]);
    var sanitizeWidgets = react_1.useMemo(function () {
        // keep the cache local so that it is cleared when the dashboard changes for example and this component is remounted
        var cache = new util_1.LRUCache({ maxSize: 100 });
        return DefaultDashboardLayoutUtils_1.getMemoizedWidgetSanitizer(cache);
    }, []);
    var transformedLayout = react_1.useMemo(function () {
        if (isExport) {
            return getDashboardLayoutForExport(layout);
        }
        return DefaultDashboardLayoutRenderer_1.DashboardLayoutBuilder.for(layout)
            .modifySections(function (section) {
            return section.modifyItems(sanitizeWidgets(getInsightByRef, enableWidgetCustomHeight));
        })
            .build();
    }, [layout, isExport, getInsightByRef, enableWidgetCustomHeight, sanitizeWidgets]);
    var widgetRenderer = react_1.useCallback(function (renderProps) {
        return (react_1.default.createElement(DashboardLayoutWidget_1.DashboardLayoutWidget, tslib_1.__assign({ onError: onError, onDrill: onDrill, onFiltersChange: onFiltersChange }, renderProps)));
    }, [onError, onDrill, onFiltersChange]);
    if (isLayoutEmpty) {
        return react_1.default.createElement(EmptyDashboardLayout_1.EmptyDashboardLayout, null);
    }
    // do not render the tailing section hotspot if there is only one section in the layout and it has only initial placeholders in it
    var shouldRenderSectionHotspot = transformedLayout.sections.length > 1 ||
        transformedLayout.sections[0].items.some(function (i) { return !widgets_1.isInitialPlaceholderWidget(i.widget); });
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement(DefaultDashboardLayoutRenderer_1.DashboardLayout, { className: isExport ? "export-mode" : "", layout: transformedLayout, itemKeyGetter: itemKeyGetter, widgetRenderer: widgetRenderer, enableCustomHeight: enableWidgetCustomHeight, sectionRenderer: RenderModeAwareDashboardLayoutSectionRenderer_1.renderModeAwareDashboardLayoutSectionRenderer, sectionHeaderRenderer: RenderModeAwareDashboardLayoutSectionHeaderRenderer_1.renderModeAwareDashboardLayoutSectionHeaderRenderer, renderMode: renderMode }),
        !!shouldRenderSectionHotspot && (react_1.default.createElement(dragAndDrop_1.SectionHotspot, { index: transformedLayout.sections.length, targetPosition: "below" }))));
};
//# sourceMappingURL=DefaultDashboardLayout.js.map